// Wiederkehrende Rechnungen (/rechnung/wiederkehrend, server/src/modules/rechnungstool/abos.ts): Typen, Adressen und
// Termine. Termine und Leistungszeitraum rechnet der Browser genau wie der Server (abo-termine.ts), für die Vorschau
// im Formular („Rechnungen am 01.11., 01.12. …, je für den Monat“).
import { MONTHS } from '../lib/format';
import type { EFormat, Kunde, Position, RtItem, Skonto } from './types';
import type { Waehrung } from './waehrung';

export type Intervall = 'monat' | 'quartal' | 'halbjahr' | 'jahr';
export type Zeitraum = 'aktuell' | 'vorher';
export type AboStatus = 'aktiv' | 'pausiert' | 'fehler' | 'beendet';

// Vorlage: das Rechnungsformular ohne Leistungsdatum. customer ist der Kunde, wie er jetzt in der Kundenliste steht
// (aus der Liste entfernt: die Anschrift beim letzten Speichern, kundeId null)
export interface AboVorlage {
  customer: Kunde | null;
  kundeId: string | null;
  eFormat?: EFormat;
  items: Position[];
  site?: string | null;
  paymentDays: number;
  note?: string | null;
  // Hinweis bei 0 % USt (fehlt bei älteren Vorlagen)
  steuerHinweis?: string | null;
  skonto?: Skonto | null;
  // Währung der Rechnungen (den Kurs nimmt jede Rechnung beim Erzeugen); fehlt: Euro
  waehrung?: Waehrung;
}

export interface Abo {
  id: string;
  titel: string;
  status: AboStatus;
  aktiv: boolean;
  // angehalten mit Fehler (z. B. Kunde weg, E-Mail nicht verschickt): bis „Weiter“
  fehler: string | null;
  // Kunde aus der Kundenliste entfernt (vor dem nächsten Termin)
  hinweis: string | null;
  // Hinweis ohne Pause (z. B. nachgeholte Rechnungen, die nicht per E-Mail gingen), bis „Erledigt“
  meldung: string | null;
  // Billtoro: wegen des Tarifs pausiert (Text in meldung, „Weiter“ erst mit passendem Tarif); sonst nicht da
  pauseGrund?: 'tarif';
  intervall: Intervall;
  zeitraum: Zeitraum;
  // Tag im Monat (31: immer am Monatsende)
  tag: number;
  naechste: string;
  // Leistungszeitraum der nächsten Rechnung (null: beendet)
  leistung: { von: string; bis: string } | null;
  // Termine vor heute ohne Rechnung (nach einer Pause oder einem Fehler); höchstens maxNachholen + 1
  verpasst: string[];
  // letzter Leistungszeitraum: Rechnungen, deren Leistungszeitraum spätestens an diesem Tag beginnt
  ende: string | null;
  versand: boolean;
  // Termin der zuletzt erzeugten Rechnung: der nächste frühestens einen Abschnitt danach
  letzterTermin: string | null;
  kundeId: string | null;
  kunde: { id: string; nummer: number; name: string; city: string; email: string | null } | null;
  kundeName: string;
  netto: number;
  brutto: number;
  // Beträge in dieser Währung
  waehrung: Waehrung;
  letzterLauf: string | null;
  letzteRechnung: { id: string; number: string } | null;
  // daraus geschriebene Rechnungen, neueste zuerst
  rechnungen: AboRechnung[];
  vorlage: AboVorlage;
  // Stand fürs Ändern (sonst 409, wenn inzwischen eine Rechnung erzeugt wurde)
  stand: string;
  createdAt: string;
}

export interface AboRechnung {
  id: string;
  number: string;
  invoiceDate: string;
  serviceFrom: string;
  serviceTo: string | null;
  brutto: number;
  waehrung: Waehrung;
  status: RtItem['status'];
}

export interface AboListe {
  heute: string;
  maxNachholen: number;
  abos: Abo[];
}

// „Wiederkehrend machen“ an einer Rechnung: Vorschlag fürs Formular
export interface AboVorschlag {
  rechnung: { id: string; number: string };
  titel: string;
  intervall: Intervall;
  zeitraum: Zeitraum;
  naechste: string;
  tag: number;
  ende: string | null;
  versand: boolean;
  vorlage: AboVorlage;
}

// „Jetzt erzeugen“
export interface JetztAntwort {
  rechnung: { id: string; number: string; amount: number; kundeId: string };
  termin: string;
  leistung: { von: string; bis: string };
  versand: null | { status: 'gesendet' } | { status: 'fehler'; fehler: string };
  abo: Abo | null;
}

export const abosUrl = (firmId: string) => `/api/rechnungstool/${firmId}/abos`;
export const aboUrl = (id: string, rest = '') => `/api/rechnungstool/abo/${id}${rest}`;
export const vorschlagUrl = (rechnungId: string, intervall: Intervall) => `/api/rechnungstool/rechnung/${rechnungId}/abo?intervall=${intervall}`;

export const INTERVALLE: [Intervall, string][] = [
  ['monat', 'Monatlich'],
  ['quartal', 'Vierteljährlich'],
  ['halbjahr', 'Halbjährlich'],
  ['jahr', 'Jährlich'],
];
export const INTERVALL_KLEIN: Record<Intervall, string> = { monat: 'monatlich', quartal: 'vierteljährlich', halbjahr: 'halbjährlich', jahr: 'jährlich' };
// Leistungszeitraum zur Auswahl: der Abschnitt des Termins oder der davor
export const ZEITRAEUME: Record<Intervall, [Zeitraum, string][]> = {
  monat: [
    ['aktuell', 'Laufender Monat'],
    ['vorher', 'Vormonat'],
  ],
  quartal: [
    ['aktuell', 'Laufendes Quartal'],
    ['vorher', 'Voriges Quartal'],
  ],
  halbjahr: [
    ['aktuell', 'Laufendes Halbjahr'],
    ['vorher', 'Voriges Halbjahr'],
  ],
  jahr: [
    ['aktuell', 'Laufendes Jahr'],
    ['vorher', 'Vorjahr'],
  ],
};

// --- Termine wie auf dem Server (abo-termine.ts) ---

const MONATE: Record<Intervall, number> = { monat: 1, quartal: 3, halbjahr: 6, jahr: 12 };
const zwei = (n: number) => String(n).padStart(2, '0');
const tageIm = (jahr: number, monat: number) => new Date(Date.UTC(jahr, monat, 0)).getUTCDate();
const teile = (d: string) => ({ jahr: Number(d.slice(0, 4)), monat: Number(d.slice(5, 7)), tag: Number(d.slice(8, 10)) });
function plusMonate(jahr: number, monat: number, n: number) {
  const i = jahr * 12 + (monat - 1) + n;
  return { jahr: Math.floor(i / 12), monat: (i % 12) + 1 };
}
const terminIm = (jahr: number, monat: number, tag: number) => `${jahr}-${zwei(monat)}-${zwei(Math.min(tag, tageIm(jahr, monat)))}`;

export const tagVon = (d: string) => teile(d).tag;
// letzter Tag seines Monats (30.11., 28.02. …)
export const istMonatsende = (d: string) => {
  const t = teile(d);
  return t.tag === tageIm(t.jahr, t.monat);
};

// Der Termin n Abschnitte später, am Tag „tag“ (gibt es ihn nicht: der letzte Tag des Monats)
export function terminPlus(termin: string, intervall: Intervall, tag: number, n = 1): string {
  const t = teile(termin);
  const m = plusMonate(t.jahr, t.monat, MONATE[intervall] * n);
  return terminIm(m.jahr, m.monat, tag);
}

// Leistungszeitraum der Rechnung zum Termin
export function leistungszeitraum(termin: string, intervall: Intervall, zeitraum: Zeitraum): { von: string; bis: string } {
  const t = teile(termin);
  const n = MONATE[intervall];
  let start = plusMonate(t.jahr, Math.floor((t.monat - 1) / n) * n + 1, 0);
  if (zeitraum === 'vorher') start = plusMonate(start.jahr, start.monat, -n);
  const ende = plusMonate(start.jahr, start.monat, n - 1);
  return { von: `${start.jahr}-${zwei(start.monat)}-01`, bis: terminIm(ende.jahr, ende.monat, 31) };
}

// Gehört der Termin noch dazu? Das Ende gilt für den Leistungszeitraum: Eine Rechnung entsteht, solange ihr
// Leistungszeitraum spätestens am Ende beginnt.
export function imLauf(a: { intervall: Intervall; zeitraum: Zeitraum; ende: string | null }, termin: string): boolean {
  return !a.ende || leistungszeitraum(termin, a.intervall, a.zeitraum).von <= a.ende;
}

// Letzter Termin vor dem Ende (ab naechste; null: ohne Ende oder schon beendet)
export function letzterTermin(a: { naechste: string; intervall: Intervall; zeitraum: Zeitraum; tag: number; ende: string | null }): string | null {
  if (!a.ende || !imLauf(a, a.naechste)) return null;
  let t = a.naechste;
  for (let n = 0; n < 1200; n++) {
    const danach = terminPlus(t, a.intervall, a.tag);
    if (!imLauf(a, danach)) return t;
    t = danach;
  }
  return t;
}

// Frühester nächster Termin nach der letzten erzeugten Rechnung (wie der Server: Monatsanfang einen Abschnitt danach,
// beim Wechsel des Rhythmus der kürzere)
export function fruehestens(letzter: string | null, alt: Intervall, neu: Intervall): string | null {
  if (!letzter) return null;
  return terminPlus(letzter, MONATE[neu] < MONATE[alt] ? neu : alt, 1);
}

// Erster Termin ab heute (verpasste übersprungen)
export function ersterTerminAb(naechste: string, intervall: Intervall, tag: number, heute: string): string {
  let t = naechste;
  while (t < heute) t = terminPlus(t, intervall, tag);
  return t;
}

// Leistungszeitraum in Worten: „November 2026“, „4. Quartal 2026“, „1. Halbjahr 2027“, „Jahr 2026“, sonst von–bis
export function zeitraumText(von: string, bis: string): string {
  const a = teile(von);
  const b = teile(bis);
  const ganz = a.tag === 1 && b.tag === tageIm(b.jahr, b.monat) && a.jahr === b.jahr;
  const monate = b.monat - a.monat + 1;
  if (ganz && monate === 1) return `${MONTHS[a.monat - 1]} ${a.jahr}`;
  if (ganz && monate === 3 && a.monat % 3 === 1) return `${(a.monat + 2) / 3}. Quartal ${a.jahr}`;
  if (ganz && monate === 6 && a.monat % 6 === 1) return `${a.monat === 1 ? 1 : 2}. Halbjahr ${a.jahr}`;
  if (ganz && monate === 12) return `Jahr ${a.jahr}`;
  const de = (d: string) => d.split('-').reverse().join('.');
  return `${de(von)} – ${de(bis)}`;
}
